import React, { useEffect, useState } from "react";
import { api } from "../api";
import { Header } from "./Shell";
import { CheckCircle2 } from "lucide-react";

const initial = {
  version_code: "",
  version_name: "",
  release_notes: "",
  channel: "testing",
  mandatory: false,
};

const formatBytes = size =>
  typeof size === "number" ? `${(size / 1024 / 1024).toFixed(1)} MB` : "—";

const errorMessage = error => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(x => x.msg).join("; ");
  return "Operazione non riuscita. Verifica la connessione e riprova.";
};

export function AndroidUpdatesAdmin({ user }) {
  const canRead = user?.permissions?.includes("admin.android_updates.read");
  const canWrite = user?.permissions?.includes("admin.android_updates.write");
  const [releases, setReleases] = useState([]);
  const [form, setForm] = useState(initial);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [testingLink, setTestingLink] = useState(null);
  const [emailRelease, setEmailRelease] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState("");


  const load = async () => {
    if (!canRead) return;
    setLoading(true);
    try {
      const response = await api.get("/admin/android-updates");
      setReleases(Array.isArray(response.data) ? response.data : response.data?.items || []);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canRead) load();
    // Permission-dependent load only.
  }, [canRead]);

  const upload = async event => {
    event.preventDefault();
    if (!canWrite || !file || busy) return;
    setBusy(true);
    setError("");
    setMessage("");
    const payload = new FormData();
    payload.append("file", file);
    payload.append("version_code", String(form.version_code));
    payload.append("version_name", form.version_name.trim());
    payload.append("release_notes", form.release_notes);
    payload.append("channel", form.channel);
    payload.append("mandatory", String(form.mandatory));
    try {
      await api.post("/admin/android-updates", payload, { timeout: 1800000 });
      setMessage("APK caricato e verificato correttamente. L'archiviazione protetta verrà completata automaticamente dal server. Una volta archiviata, la versione potrà essere pubblicata.");
      setForm(initial);
      setFile(null);
      event.target.reset();
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const generateTestingLink = async release => {
    if (!canRead || busy || release.channel !== "testing" || !release.published) return;

    setBusy(true);
    setError("");
    setTestingLink(null);

    try {
      const response = await api.post(
        `/admin/android-updates/testing/${release.version_code}/download-link`
      );

      const relativeUrl = response.data?.download_url;
      if (typeof relativeUrl !== "string") {
        throw new Error("Il server non ha restituito un link valido.");
      }

      const url = new URL(relativeUrl, window.location.origin);

      if (
        url.origin !== window.location.origin ||
        url.pathname !== `/api/android-updates/testing/download/${release.version_code}` ||
        url.protocol !== "https:"
      ) {
        throw new Error("Collegamento Testing non valido.");
      }

      const expires = Number(response.data?.expires_in_seconds || 300);

      setTestingLink({
        version: release.version_name,
        url: url.toString(),
        expiresAt: Date.now() + expires * 1000
      });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const copyTestingLink = async () => {
    if (!testingLink) return;

    if (Date.now() >= testingLink.expiresAt) {
      setTestingLink(null);
      setError("Il collegamento Testing è scaduto. Generane uno nuovo.");
      return;
    }

    try {
      await navigator.clipboard.writeText(testingLink.url);
      setMessage("Collegamento Testing copiato negli appunti.");
    } catch {
      setError("Impossibile copiare automaticamente il collegamento.");
    }
  };

  const sendProductionEmail = async event => {
    event.preventDefault();

    if (!canWrite || busy || !emailRelease) return;

    setBusy(true);
    setError("");
    setMessage("");

    try {
      await api.post(
        `/admin/android-updates/production/${emailRelease.version_code}/email-link`,
        { email: recipientEmail.trim() }
      );

      setMessage(
        `Email di download inviata a ${recipientEmail.trim()}. ` +
        "Il collegamento è valido 15 minuti e utilizzabile una sola volta."
      );

      setEmailRelease(null);
      setRecipientEmail("");

    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const publish = async release => {
    if (!canWrite || busy) return;
    if (!window.confirm(`Pubblicare la versione ${release.version_name} (${release.version_code}) sul canale ${release.channel}?`)) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await api.post(`/admin/android-updates/${release.version_code}/publish`);
      setMessage(`Versione ${release.version_name} pubblicata sul canale ${release.channel}.`);
      await load();
    } catch (err) {
      setError(`${errorMessage(err)} Se l'APK non è ancora archiviato e confermato, completare prima tale operazione sul server.`);
    } finally {
      setBusy(false);
    }
  };

  return <>
    <Header showStatus={false} kicker="AMMINISTRAZIONE / ANDROID" title="Aggiornamenti Android"
      subtitle="Carica, verifica e pubblica le nuove versioni dell'app 118 Finder." />
    {!canRead ? <p>Non disponi del permesso per consultare gli aggiornamenti Android.</p> : <>
      {error && <p role="alert" style={{color:"var(--danger, #b42318)"}}>{error}</p>}
      {message && (
        <div
          role="status"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "14px 18px",
            marginBottom: 20,
            backgroundColor: "#ecfdf3",
            border: "1px solid #a6e9c1",
            borderLeft: "4px solid #16a34a",
            borderRadius: 8,
            color: "#166534",
            fontSize: 14,
            lineHeight: 1.5
          }}
        >
          <CheckCircle2
            size={22}
            style={{ flexShrink: 0, color: "#16a34a" }}
          />
          <span>{message}</span>
        </div>
      )}
      {canWrite && <section className="admin-overview" style={{marginBottom:24}}>
        <h2>Carica una nuova versione</h2>
        <form onSubmit={upload} className="user-create-form">
          <label>File APK firmato
            <input type="file" accept=".apk,application/vnd.android.package-archive"
              required onChange={e=>setFile(e.target.files?.[0] || null)} />
          </label>
          <label>Codice versione
            <input type="number" min="2" step="1" required value={form.version_code}
              onChange={e=>setForm({...form,version_code:e.target.value})} />
          </label>
          <label>Nome versione
            <input type="text" maxLength="30" required placeholder="2.0.0"
              value={form.version_name}
              onChange={e=>setForm({...form,version_name:e.target.value})} />
          </label>
          <label>Canale
            <select value={form.channel}
              onChange={e=>setForm({...form,channel:e.target.value})}>
              <option value="testing">Testing</option>
              <option value="production">Produzione</option>
            </select>
          </label>
          <label>Note di rilascio
            <textarea maxLength="5000" rows="3" value={form.release_notes}
              onChange={e=>setForm({...form,release_notes:e.target.value})} />
          </label>
          <label
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
              gap: 10,
              width: "fit-content",
              cursor: "pointer",
              marginBottom: 12
            }}
          >
            <input
              type="checkbox"
              checked={form.mandatory}
              onChange={e=>setForm({...form,mandatory:e.target.checked})}
              style={{
                width: 17,
                height: 17,
                margin: 0,
                padding: 0,
                flexShrink: 0,
                accentColor: "#2563eb"
              }}
            />
            <span style={{
              fontSize: 14,
              fontWeight: 500,
              textTransform: "none",
              letterSpacing: "normal"
            }}>
              Aggiornamento obbligatorio
            </span>
          </label>
          <button type="submit" className="primary-btn"
            disabled={busy || !file}>{busy ? "Operazione in corso…" : "Carica e verifica APK"}</button>
        </form>
        <p><small>Il caricamento non pubblica automaticamente la versione.
          L'archiviazione protetta viene eseguita automaticamente dal server.
          Quando lo stato è "Archiviata", puoi procedere alla pubblicazione.</small></p>
      </section>}
      <section>
        <div className="admin-toolbar">
          <h2>Versioni caricate</h2>
          <button type="button" onClick={load} disabled={loading || busy}>
            {loading ? "Aggiornamento…" : "Aggiorna elenco"}
          </button>
        </div>
        {testingLink && (
          <div
            style={{
              padding: 16,
              marginBottom: 16,
              border: "1px solid var(--border, #cbd5e1)",
              borderRadius: 8
            }}
          >
            <strong>Download Testing — versione {testingLink.version}</strong>
            <p>Collegamento temporaneo valido per 5 minuti dalla generazione.</p>
            <input
              type="text"
              readOnly
              value={testingLink.url}
              aria-label="Collegamento download Testing"
              style={{width: "100%", marginBottom: 10}}
              onFocus={event => event.target.select()}
            />
            <div style={{display: "flex", gap: 10, flexWrap: "wrap"}}>
              <button type="button" onClick={copyTestingLink}>
                Copia link
              </button>
              <button
                type="button"
                onClick={() => {
                  if (Date.now() >= testingLink.expiresAt) {
                    setTestingLink(null);
                    setError("Il collegamento Testing è scaduto. Generane uno nuovo.");
                    return;
                  }
                  window.open(testingLink.url, "_blank", "noopener,noreferrer");
                }}
              >
                Apri download
              </button>
            </div>
          </div>
        )}
        {emailRelease && (
          <form
            onSubmit={sendProductionEmail}
            className="admin-overview"
            style={{ marginBottom: 20, padding: 20 }}
          >
            <h3>
              Invia APK Production {emailRelease.version_name}
            </h3>

            <p>
              Il destinatario riceverà un collegamento temporaneo,
              valido 15 minuti e utilizzabile una sola volta.
            </p>

            <label htmlFor="android-email-recipient">
              Indirizzo email del destinatario
            </label>

            <input
              id="android-email-recipient"
              type="email"
              required
              maxLength={254}
              autoComplete="email"
              value={recipientEmail}
              onChange={event => setRecipientEmail(event.target.value)}
              placeholder="nome@dominio.it"
              style={{
                width: "100%",
                maxWidth: 420,
                marginTop: 8,
                marginBottom: 16
              }}
            />

            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <button
                type="submit"
                className="primary-btn"
                disabled={busy || !recipientEmail.trim()}
              >
                {busy ? "Invio in corso..." : "Invia email"}
              </button>

              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setEmailRelease(null);
                  setRecipientEmail("");
                }}
              >
                Annulla
              </button>
            </div>
          </form>
        )}

        <div className="table-wrap">
          <table>
            <thead><tr><th>Versione</th><th>Canale</th><th>Dimensione</th>
              <th>Stato</th><th>Obbligatorio</th><th>Azioni</th></tr></thead>
            <tbody>
              {releases.map(release => <tr key={release.version_code}>
                <td><b>{release.version_name}</b><small>Codice {release.version_code}</small></td>
                <td>{release.channel === "production" ? "Produzione" : "Testing"}</td>
                <td>{formatBytes(release.size)}</td>
                <td>{release.published ? "Pubblicata" : release.archived ? "Archiviata" : "In attesa di archiviazione"}</td>
                <td>{release.mandatory ? "Sì" : "No"}</td>
                <td>
                  {canWrite &&
                    release.archived &&
                    !release.published &&
                    !releases.some(other =>
                      other.channel === release.channel &&
                      other.published &&
                      other.version_code >= release.version_code
                    ) && (
                    <button type="button" disabled={busy} onClick={()=>publish(release)}>
                      Pubblica
                    </button>
                  )}
                  {canWrite &&
                    release.channel === "production" &&
                    release.published &&
                    release.archived && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setEmailRelease(release);
                        setRecipientEmail("");
                        setError("");
                      }}
                    >
                      Invia APK via email
                    </button>
                  )}
                  {canRead && release.channel === "testing" && release.published && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => generateTestingLink(release)}
                    >
                      Genera link Testing
                    </button>
                  )}
                </td>
              </tr>)}
              {!releases.length && <tr><td colSpan="6">Nessuna versione caricata.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </>}
  </>;
}
